import type { NetworkDashboardAvailableStore } from '@gozoki/api-types'
import {
  NETWORK_COMPARISON_MODES,
  networkComparisonPeriod,
  type NetworkComparisonMode,
} from '@gozoki/api-types/src/network-comparison'
import { isMatureAt } from '~/components/dashboards/filters/store-age'
import { parseDay, weekPeriodParts, type WeekRange } from './week-period-model'

export const COMPARISON_CHOICES = [...NETWORK_COMPARISON_MODES, 'none'] as const
export type ComparisonChoice = (typeof COMPARISON_CHOICES)[number]

export const COMPARISON_UNIVERSES = ['same_stores', 'comparables'] as const
export type ComparisonUniverse = (typeof COMPARISON_UNIVERSES)[number]

export interface Comparison {
  choice: ComparisonChoice
  universe: ComparisonUniverse
}

export interface ComparedPeriods {
  periodStart: string
  periodEnd: string
  compStart: string
  compEnd: string
}

export function comparisonRangeTag({
  periodStart,
  periodEnd,
  compStart,
  compEnd,
}: ComparedPeriods): string {
  const { weeks, days } = weekPeriodParts({ startDate: compStart, endDate: compEnd })
  const weekAligned = parseDay(periodStart).weekday === 1 && parseDay(compStart).weekday === 1
  const yearOf = (date: string) => (weekAligned ? parseDay(date).weekYear : parseDay(date).year)
  const tag = weekAligned ? weeks : days
  return yearOf(compEnd) === yearOf(periodEnd) ? tag : `${tag} ${yearOf(compEnd)}`
}

export const comparisonRangeLabel = (bounds: ComparedPeriods | null): string =>
  bounds ? `vs ${comparisonRangeTag(bounds)}` : ''

export function comparisonPeriodTag(range: WeekRange, mode: NetworkComparisonMode): string {
  const { compStart, compEnd } = networkComparisonPeriod(range.startDate, range.endDate, mode)
  return comparisonRangeTag({
    periodStart: range.startDate,
    periodEnd: range.endDate,
    compStart,
    compEnd,
  })
}

export function comparisonButtonLabel(range: WeekRange, choice: ComparisonChoice): string {
  return choice === 'none' ? 'Sans comparaison' : `vs ${comparisonPeriodTag(range, choice)}`
}

export interface ComparisonMenuState {
  open: boolean
  pressedKey: string | null
}

export type ComparisonMenuEvent =
  | { type: 'openChange'; open: boolean }
  | { type: 'keydown'; key: string; repeat: boolean }
  | { type: 'keyup'; key: string }
  | { type: 'click'; detail: number }

export const CLOSED_COMPARISON_MENU: ComparisonMenuState = { open: false, pressedKey: null }

const CONFIRM_KEYS = new Set(['Enter', ' '])

// Arrows check radios via synthetic clicks (detail 0); the keyup of the Enter that opened the menu has no keydown here.
export function comparisonMenuReducer(
  state: ComparisonMenuState,
  event: ComparisonMenuEvent,
): ComparisonMenuState {
  switch (event.type) {
    case 'openChange': {
      return { open: event.open, pressedKey: null }
    }
    case 'keydown': {
      return event.repeat ? state : { ...state, pressedKey: event.key }
    }
    case 'keyup': {
      return event.key === state.pressedKey && CONFIRM_KEYS.has(event.key)
        ? CLOSED_COMPARISON_MENU
        : { ...state, pressedKey: null }
    }
    case 'click': {
      return event.detail > 0 ? CLOSED_COMPARISON_MENU : state
    }
  }
}

export function applyComparisonUniverse(
  includedIds: number[] | undefined,
  stores: NetworkDashboardAvailableStore[],
  universe: ComparisonUniverse,
  compStart: string,
): number[] | undefined {
  if (universe === 'same_stores') return includedIds
  const candidates =
    includedIds === undefined ? stores : stores.filter((store) => includedIds.includes(store.id))
  return candidates.filter(isMatureAt(compStart)).map((store) => store.id)
}
